import styled from "@emotion/styled";
import React, { useState } from "react";
import { observer } from "mobx-react-lite";
import { useFieldsScreenVM } from "@screens/FieldsScreen/FieldsScreenVM";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row } from "@src/components/Flex";
import { useConvert, useLang, useYield } from "@src/hooks/useLang";
import getTextWidth from "@src/utils/getTextWidth";
import { trunc } from "@src/utils/BN";
import useIsMobile from "@src/hooks/useIsMobile";
import { ReactComponent as ExpandMoreIcon } from "@assets/icons/expand_more.svg";
import { ReactComponent as ArrowLeftIcon } from "@assets/icons/arrowLeft.svg";
import { CUSTOM_PANEL_HEIGHT } from "@screens/FieldsScreen/CustomLayerPanel";

interface IProps {}

const Root = styled.div<{ width?: number; topOffset?: number }>`
  position: absolute;
  inset: ${({ topOffset }) => 18 + (topOffset ?? 0)}px
    max(52px, env(safe-area-inset-right, 52px)) auto auto;
  background: #fff;
  padding: 6px 12px;
  width: ${({ width }) => width ?? 184}px;
  border-radius: 6px;
  z-index: 2;
`;

const CollapsedRoot = styled.div<{ topOffset?: number }>`
  position: absolute;
  inset: ${({ topOffset }) => 18 + (topOffset ?? 0)}px
    max(52px, env(safe-area-inset-right, 52px)) auto auto;
  background: #fff;
  padding: 8px 10px;
  border-radius: 6px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  max-width: 160px;
`;

// Shared with FieldTracksPanel so both map panels hide the same way.
export const HideLegendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  background: #f0f2f3;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 11px;
  color: #899093;
  margin-top: 4px;
  align-self: flex-end;

  &:hover {
    background: #e4e6e8;
  }
`;

export const ShowLegendButton = styled.button<{ topOffset?: number }>`
  position: absolute;
  inset: ${({ topOffset }) => 18 + (topOffset ?? 0)}px
    max(52px, env(safe-area-inset-right, 52px)) auto auto;
  background: #fff;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 11px;
  color: #494c5b;
  z-index: 2;
  box-shadow: 0 2px 10px rgba(78, 78, 81, 0.1);
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background: #f0f2f3;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const ColorStrip = styled.div`
  display: flex;
  width: 100%;
  height: 12px;
  border-radius: 4px;
  overflow: hidden;
`;

const ExpandButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #f0f2f3;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 12px;
  color: #494c5b;

  &:active {
    background: #e4e6e8;
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    fill: currentColor;
  }
`;

const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  margin-bottom: 4px;
  background: #68ad18;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #fff;
  align-self: flex-start;

  &:active {
    background: #5a9514;
  }

  svg {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    fill: currentColor;
  }
  svg path {
    fill: currentColor;
  }
`;

const ScaleToggle = styled.div`
  display: flex;
  background: #f0f2f3;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 6px;
`;

const ScaleOption = styled.button<{ active?: boolean }>`
  flex: 1;
  padding: 3px 8px;
  border: none;
  background: ${({ active }) => (active ? "#68ad18" : "transparent")};
  color: ${({ active }) => (active ? "#fff" : "#494c5b")};
  font-family: Nunito Sans, sans-serif;
  font-weight: 700;
  font-size: 11px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;

  &:hover {
    background: ${({ active }) => (active ? "#5a9514" : "#e4e6e8")};
  }
`;

const font =
  "font-family: Nunito Sans; font-weight: bold;font-size: 12px; line-height: 18px;white-space:nowrap";


const MapYieldLegend: React.FC<IProps> = () => {
  const vm = useFieldsScreenVM();
  const lang = useLang();
  const { convertYield } = useConvert();
  const locYield = useYield();
  const isMobile = useIsMobile();
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const [legendHidden, setLegendHidden] = useState(false);

  const legend =
    vm.selectedFieldId != null
      ? vm.heatmapLegend[String(vm.selectedFieldId)]
      : undefined;
  const max =
    legend != null ? Math.max(...legend.map(({ percent }) => percent)) : 0;

  // Camera layers show raw values (counts / % / size), not yield units.
  const isCameraLayer = vm.isCameraView(vm.selectedFieldView);
  // Custom layers are the same story: the number came from the customer's own
  // file in whatever unit they typed, so it must never be converted.
  const isCustomLayer = vm.isCustomView(vm.selectedFieldView);
  const customUnit = isCustomLayer ? vm.activeCustomLayer?.unitLabel ?? "" : "";
  // CustomLayerPanel occupies the top of this column when a custom layer is on
  // screen, so the legend starts below it.
  const topOffset =
    isCustomLayer && vm.activeCustomLayer ? CUSTOM_PANEL_HEIGHT + 8 : 0;
  const cameraUnit = (() => {
    if (!isCameraLayer) return "";
    if (vm.selectedFieldView === "potato_count")
      return lang?.fields?.cameraUnitPcs ?? "pcs";
    if (vm.selectedFieldView === "stone_percent") return "%";
    const meta = vm.selectedFieldId != null
      ? vm.cameraStatistics[String(vm.selectedFieldId)]?.meta
      : null;
    return meta?.size_unit ?? "px";
  })();

  const formatRange = (start: number, end: number): string => {
    if (isCameraLayer || isCustomLayer) {
      return `${trunc(start, 1)} - ${trunc(end, 1)}`;
    }
    const range = `${trunc(convertYield(start))} - ${trunc(convertYield(end))}`;
    return range;
  };

  const maxWidth = Math.max(
    ...(legend ?? []).map(({ start, end }) => {
      return getTextWidth(formatRange(start, end), font);
    })
  );

  const legendExpandLabel = lang?.fields?.legendExpandLabel ?? "Show legend";
  const legendCollapseLabel = lang?.fields?.legendCollapseLabel ?? "Back";
  const legendHideLabel = lang?.fields?.legendHideLabel ?? "Hide";
  const legendShowLabel = lang?.fields?.legendShowLabel ?? "Show legend";
  const scaleNormalLabel = lang?.fields?.scaleNormalLabel ?? "Normal";
  const scaleContrastLabel = lang?.fields?.scaleContrastLabel ?? "Contrast";

  if (
    (vm.selectedFieldView !== "yield" && !isCameraLayer && !isCustomLayer) ||
    legend == null ||
    legend.length === 0
  ) {
    return null;
  }

  // Desktop: hidden state - show a small "Show legend" button
  if (!isMobile && legendHidden) {
    return (
      <ShowLegendButton topOffset={topOffset} onClick={() => setLegendHidden(false)}>
        <ExpandMoreIcon />
        {legendShowLabel}
      </ShowLegendButton>
    );
  }

  // Mobile: collapsed by default, show compact color strip + expand button
  if (isMobile && !mobileExpanded) {
    return (
      <CollapsedRoot topOffset={topOffset}>
        <ColorStrip>
          {legend.map((v, i) => (
            <div
              key={i}
              style={{
                flex: v.percent,
                background: v.color,
                minWidth: 2,
              }}
              title={`${formatRange(v.start, v.end)}: ${v.percent}%`}
            />
          ))}
        </ColorStrip>
        <ExpandButton onClick={() => setMobileExpanded(true)}>
          <ExpandMoreIcon />
          {legendExpandLabel}
        </ExpandButton>
      </CollapsedRoot>
    );
  }

  // Mobile expanded or desktop: full legend
  return (
    <Root width={maxWidth * 2 + 56} topOffset={topOffset}>
      {isMobile && (
        <CollapseButton onClick={() => setMobileExpanded(false)}>
          <ArrowLeftIcon />
          {legendCollapseLabel}
        </CollapseButton>
      )}
      <Text type="heading" color="#899093" size={12}>
        {isCustomLayer
          ? customUnit || vm.activeCustomLayer?.name || ""
          : isCameraLayer
          ? cameraUnit
          : locYield()}
      </Text>
      <SizedBox height={4} />
      {/* Custom layers are bucketed once, at import — there is nothing to
          re-scale, so the toggle would be a dead control. */}
      {!isCustomLayer && (
        <ScaleToggle>
          <ScaleOption
            active={vm.yieldScale === "quantize"}
            onClick={() => vm.setYieldScale("quantize")}
          >
            {scaleNormalLabel}
          </ScaleOption>
          <ScaleOption
            active={vm.yieldScale === "quantile"}
            onClick={() => vm.setYieldScale("quantile")}
          >
            {scaleContrastLabel}
          </ScaleOption>
        </ScaleToggle>
      )}
      {legend.map((v, i) => (
        <Row style={{ height: 16, marginBottom: 6 }} key={i}>
          <Text
            color="#494C5B"
            size={11}
            type="heading"
            style={{ marginRight: 8 }}
            nowrap
          >
            {formatRange(v.start, v.end)}
          </Text>
          <Row
            style={{
              height: "100%",
              padding: "0 4px",
              position: "relative",
            }}
            alignItems="center"
          >
            <div
              style={{
                position: "absolute",
                background: v.color,
                top: 0,
                bottom: 0,
                left: 0,
                width: `${(v.percent / max) * 100}%`,
              }}
            />
            <Text
              color="#494C5B"
              size={11}
              type="heading"
              style={{
                marginRight: 8,
                zIndex: 1,
                textShadow:
                  "1px 0 0 #fff, " +
                  "-1px 0 0 #fff, " +
                  "0 1px 0 #fff, " +
                  "0 -1px 0 #fff, " +
                  "1px 1px #fff, " +
                  "-1px -1px 0 #fff, " +
                  "1px -1px 0 #fff, " +
                  "-1px 1px 0 #fff",
              }}
            >
              {v.percent} %
            </Text>
          </Row>
        </Row>
      ))}
      {!isMobile && (
        <HideLegendButton onClick={() => setLegendHidden(true)}>
          {legendHideLabel}
        </HideLegendButton>
      )}
    </Root>
  );
};
export default observer(MapYieldLegend);
